import React, { useEffect } from 'react';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  LexicalEditor,
} from 'lexical';
import { OnChangePlugin as LexicalOnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isListNode, ListItemNode } from '@lexical/list';

const IGNORE_MARKUP = '<p class="iva-editor-paragraph"><br></p>';

export type Data = {
  value: string;
  editor: LexicalEditor;
};

export type Props = {
  ignoreSelectionChange?: boolean;
  ignoreInitialChange?: boolean;
  onChange?: (html: string, data: Data) => void;
  onInit?: (editor: LexicalEditor) => void;
  html?: string | null;
  disable?: boolean;
  shouldEmpty?: boolean;
  shoulFocus?: boolean;
};

const OnchangePlugin: React.FC<Props> = ({
  ignoreInitialChange = true,
  ignoreSelectionChange = true,
  onChange,
  html,
  onInit,
  disable,
  shouldEmpty,
  shoulFocus,
}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disable);
  }, [editor, disable]);

  useEffect(() => {
    editor.update(() => {
      if (html) {
        const parser = new DOMParser();
        const nodes = $generateNodesFromDOM(
          editor,
          parser.parseFromString(html.split('<br>').join(''), 'text/html'),
        );

        // Parse list item
        const newNodes = nodes.map((node) => {
          if ($isListNode(node)) {
            const listItemNodes = node.getChildren() as ListItemNode[];
            const newListItems = listItemNodes
              .filter((itemNode) => itemNode.getChildren().length)
              .map((itemNode, index) => {
                const writable = itemNode.getWritable();
                writable.setValue(index + 1);

                return writable;
              });

            const listWritable = node
              .getWritable()
              .clear()
              .append(...newListItems);

            return listWritable;
          }

          return node;
        });

        // $removeHighestEmptyListParent();
        // if text content === '', append nodes
        if ($getRoot().getTextContent()) return;
        // Select the root
        $getRoot().select();
        // Insert them at a selection.
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) return;
        selection.insertNodes(newNodes);
      }

      if (html === '' && shouldEmpty) {
        $getRoot().clear().append($createParagraphNode());
      }

      if (shoulFocus === false) {
        $setSelection(null);
      }
    });
  }, [html, shouldEmpty, shoulFocus]);

  return (
    <LexicalOnChangePlugin
      ignoreSelectionChange={ignoreSelectionChange}
      onChange={(editorState, editor) => {
        editor.update(() => {
          const htmlEditor = $generateHtmlFromNodes(editor, null);
          const value = htmlEditor === IGNORE_MARKUP ? '' : htmlEditor;

          onChange?.(value, { value, editor });
        });
      }}
    />
  );
};

export default OnchangePlugin;
